import { Link } from "react-router-dom";
import { Heart, ShieldCheck, Users, Globe2 } from "lucide-react";
import { BangladeshFlag, SwedenFlag } from "@/components/Flags";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative">
        <div className="bg-stage" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="bg-grid" />
          <div className="orb orb-green" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-12 sm:pt-28 sm:pb-16">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)] mb-3">About us</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Two countries.<br />
            <span className="text-[color:var(--bd-green-bright)]">One</span> trusted travel partner.
          </h1>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-12 grid md:grid-cols-2 gap-10 items-start">
          <div className="space-y-5 text-[color:var(--ivory)] leading-relaxed">
            <p className="text-lg">
              Bangladesh Travels &amp; Tours AB is a Swedish-registered travel boutique with deep roots in Bangladesh.
              We were founded by travellers who got tired of cookie-cutter package tours and impersonal flight portals.
            </p>
            <p className="text-[color:var(--ivory-muted)]">
              Today we serve families relocating between Stockholm and Dhaka, business travellers, pilgrims heading to
              Makkah and Madina, students starting their studies, and dreamers chasing northern lights or Bengal tigers.
            </p>
            <p className="text-[color:var(--ivory-muted)]">
              Every itinerary is built by a real human who has either lived in or travelled the route. Every booking
              comes with someone you can actually call — in Swedish, English, Bengali or Hindi.
            </p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 space-y-6">
            <div className="flex items-center gap-3">
              <BangladeshFlag className="w-9 h-6 rounded-sm" />
              <div>
                <div className="font-display text-lg">Bangladesh office</div>
                <div className="text-sm text-[color:var(--ivory-muted)]">Dhaka — ground operations &amp; customer support</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SwedenFlag className="w-9 h-6 rounded-sm" />
              <div>
                <div className="font-display text-lg">Sweden office</div>
                <div className="text-sm text-[color:var(--ivory-muted)]">Stockholm — bookings, visa &amp; customer care</div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-6 text-sm text-[color:var(--ivory-muted)]">
              Registered company in Sweden · IATA-accredited partners · GDPR compliant
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 border-y border-white/5 bg-[#0A0D11]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <h2 className="font-display text-4xl sm:text-5xl mb-12">What we stand for</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: "Care first", text: "We answer your questions before we send you a quote." },
              { icon: ShieldCheck, title: "Honest pricing", text: "What you see is what you pay. No hidden fees." },
              { icon: Users, title: "Real humans", text: "A person handles your booking — and is reachable when you travel." },
              { icon: Globe2, title: "Local depth", text: "Our network in Bangladesh means the gates open for our travellers." },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={i} className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
                  <div className="w-10 h-10 rounded-xl bg-[color:var(--bd-green)]/15 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[color:var(--bd-green-bright)]" />
                  </div>
                  <div className="font-display text-xl mb-2">{v.title}</div>
                  <p className="text-sm text-[color:var(--ivory-muted)]">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24 text-center">
        <div className="max-w-[800px] mx-auto px-5">
          <h2 className="font-display text-3xl sm:text-4xl">Ready to travel with us?</h2>
          <p className="text-[color:var(--ivory-muted)] mt-4">Tell us where you want to go — we'll handle the rest.</p>
          <Link to="/contact" className="pill pill-primary inline-flex items-center gap-2 mt-8">Plan your trip</Link>
        </div>
      </section>
    </div>
  );
}
